import React, { useEffect, useMemo, useState } from "react";
import { BOARD } from "../../data/board";
import { connections, linkedNodes, stringPath } from "../../data/connections";
import { useBoardGeometry } from "./BoardGeometry";

/** Which pinned card the pointer or keyboard focus is on, if any. */
const useTracedNode = (board) => {
  const [traced, setTraced] = useState(null);

  useEffect(() => {
    if (!board) return undefined;
    const nodeOf = (target) => target?.closest?.("[data-node]")?.dataset.node ?? null;
    const onEnter = (event) => setTraced(nodeOf(event.target));
    const onLeave = (event) => {
      if (nodeOf(event.relatedTarget) !== nodeOf(event.target)) setTraced(nodeOf(event.relatedTarget));
    };

    board.addEventListener("pointerover", onEnter);
    board.addEventListener("pointerout", onLeave);
    board.addEventListener("focusin", onEnter);
    board.addEventListener("focusout", onLeave);
    return () => {
      board.removeEventListener("pointerover", onEnter);
      board.removeEventListener("pointerout", onLeave);
      board.removeEventListener("focusin", onEnter);
      board.removeEventListener("focusout", onLeave);
    };
  }, [board]);

  return traced;
};

export const ConnectionLayer = () => {
  const { anchors, board } = useBoardGeometry();
  const traced = useTracedNode(board);
  const tracing = Boolean(traced && linkedNodes[traced]);

  const paths = useMemo(
    () =>
      connections.flatMap((connection, index) => {
        const from = anchors[connection.from];
        const to = anchors[connection.to];
        return from && to ? [{ ...connection, d: stringPath(from, to, index + 1) }] : [];
      }),
    [anchors]
  );

  // Mark the cards at the other end of the traced strings.
  useEffect(() => {
    if (!board || !tracing) return undefined;
    const linked = [...board.querySelectorAll("[data-node]")].filter((node) =>
      linkedNodes[traced].has(node.dataset.node)
    );
    linked.forEach((node) => node.classList.add("is-linked"));
    return () => linked.forEach((node) => node.classList.remove("is-linked"));
  }, [board, traced, tracing]);

  return (
    <svg
      className={`connection-layer${tracing ? " is-tracing" : ""}`}
      viewBox={`0 0 ${BOARD.width} ${BOARD.height}`}
      width={BOARD.width}
      height={BOARD.height}
      aria-hidden="true"
    >
      {paths.map((path) => {
        const live = tracing && (path.from === traced || path.to === traced);
        return <path key={path.id} d={path.d} className={`connection-path is-${path.type}${live ? " is-live" : ""}`} />;
      })}
    </svg>
  );
};

export default ConnectionLayer;
